(() => {
  if (!customElements.get('eb-home-slider')) {
    class EbHomeSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-ebh-track]');
        this.slides = [...this.querySelectorAll('[data-ebh-slide]')];
        this.dots = [...this.querySelectorAll('[data-ebh-dot]')];
        this.index = 0;
        this.pointerStart = null;
        this.autoplay = this.dataset.autoplay === 'true';
        this.delay = Math.max(3000, Number(this.dataset.delay || 6000));
        this.querySelector('[data-ebh-prev]')?.addEventListener('click', () => this.go(this.index - 1));
        this.querySelector('[data-ebh-next]')?.addEventListener('click', () => this.go(this.index + 1));
        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.go(index)));
        this.addEventListener('pointerdown', event => { this.pointerStart = event.clientX; });
        this.addEventListener('pointerup', event => {
          if (this.pointerStart === null) return;
          const distance = event.clientX - this.pointerStart;
          if (Math.abs(distance) > 48) this.go(this.index + (distance < 0 ? 1 : -1));
          this.pointerStart = null;
        });
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
        document.addEventListener('shopify:block:select', event => {
          const slide = event.target.closest?.('[data-ebh-slide]');
          if (slide && this.contains(slide)) this.go(this.slides.indexOf(slide));
        });
        this.go(0, false);
        this.start();
      }

      disconnectedCallback() { this.stop(); }

      go(index, animate = true) {
        if (!this.slides.length) return;
        this.index = (index + this.slides.length) % this.slides.length;
        if (!animate) this.track.style.transition = 'none';
        this.track.style.transform = `translate3d(-${this.index * 100}%,0,0)`;
        this.slides.forEach((slide, slideIndex) => slide.setAttribute('aria-hidden', slideIndex === this.index ? 'false' : 'true'));
        this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === this.index ? 'true' : 'false'));
        if (!animate) requestAnimationFrame(() => { this.track.style.transition = ''; });
        this.start();
      }

      start() {
        this.stop();
        if (!this.autoplay || this.slides.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.timer = setInterval(() => this.go(this.index + 1), this.delay);
      }

      stop() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }
    }
    customElements.define('eb-home-slider', EbHomeSlider);
  }

  if (!customElements.get('eb-home-sets')) {
    class EbHomeSets extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[data-ebh-set-tab]')];
        this.cards = [...this.querySelectorAll('[data-ebh-set-card]')];
        this.tabs.forEach(tab => tab.addEventListener('click', () => this.show(tab.dataset.ebhSetTab)));
        this.show(this.dataset.defaultGroup || this.tabs[0]?.dataset.ebhSetTab || 'classic');
      }

      show(group) {
        this.tabs.forEach(tab => tab.setAttribute('aria-selected', tab.dataset.ebhSetTab === group ? 'true' : 'false'));
        this.cards.forEach(card => { card.hidden = card.dataset.group !== group; });
      }
    }
    customElements.define('eb-home-sets', EbHomeSets);
  }

  if (!window.__ebHomeSizePickerReady) {
    window.__ebHomeSizePickerReady = true;
    let activePicker = null;

    const storeRoot = () => window.Shopify?.routes?.root || '/';

    const cartSummary = cart => ({
      id: cart.token || 'ajax-cart',
      totalQuantity: Number(cart.item_count || 0),
      cost: {
        totalAmount: {
          amount: (Number(cart.total_price || 0) / 100).toFixed(2),
          currencyCode: cart.currency || 'RON'
        }
      },
      lines: (cart.items || []).map(item => ({
        id: item.key || String(item.id),
        quantity: Number(item.quantity || 0),
        cost: {
          totalAmount: {
            amount: (Number(item.final_line_price || 0) / 100).toFixed(2),
            currencyCode: cart.currency || 'RON'
          }
        }
      })),
      discountCodes: (cart.discount_codes || []).map(discount => ({
        code: discount.code,
        applicable: Boolean(discount.applicable)
      }))
    });

    const publishCartUpdate = (source, cart, variantId) => {
      const update = new Event('shopify:cart:lines-update', { bubbles: true });
      Object.defineProperties(update, {
        action: { value: 'add' },
        context: { value: 'product' },
        lines: { value: [{ merchandiseId: String(variantId), quantity: 1 }] },
        promise: {
          value: Promise.resolve({
            cart: cartSummary(cart),
            detail: {
              items: cart.items || [],
              itemCount: Number(cart.item_count || 0),
              source: 'ebprime-home',
              sourceId: source.closest('.shopify-section')?.id || 'ebprime-home',
              didError: false
            }
          })
        }
      });
      document.dispatchEvent(update);
    };

    const closePicker = dialog => {
      if (dialog?.open) dialog.close();
    };

    document.addEventListener('click', async event => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const openButton = target.closest('[data-ebh-size-open]');
      if (openButton) {
        const section = openButton.closest('.ebh-popular-wrap');
        const card = openButton.closest('.ebh-product');
        const dialog = section?.querySelector('[data-ebh-size-dialog]');
        const variantsNode = card?.querySelector('[data-ebh-variants]');
        if (!(dialog instanceof HTMLDialogElement) || !variantsNode) return;

        let variants = [];
        try {
          variants = JSON.parse(variantsNode.textContent || '[]');
        } catch (_) {
          return;
        }

        const byLabel = new Map();
        variants.forEach(variant => {
          const label = String(variant.label || 'Marime unica');
          const current = byLabel.get(label);
          if (!current || (!current.available && variant.available)) byLabel.set(label, variant);
        });

        const options = dialog.querySelector('[data-ebh-size-options]');
        const title = dialog.querySelector('[data-ebh-size-title]');
        const confirm = dialog.querySelector('[data-ebh-size-confirm]');
        const status = dialog.querySelector('[data-ebh-size-status]');
        if (!options || !title || !confirm || !status) return;

        title.textContent = openButton.dataset.productTitle || 'Alege marimea';
        options.replaceChildren();
        status.textContent = '';
        status.classList.remove('is-error');
        confirm.disabled = true;
        activePicker = { dialog, trigger: openButton, selected: null };

        byLabel.forEach(variant => {
          const option = document.createElement('button');
          option.type = 'button';
          option.className = 'ebh-size-dialog__option';
          option.textContent = String(variant.label || 'Marime unica');
          option.dataset.variantId = String(variant.id);
          option.disabled = !variant.available;
          option.setAttribute('aria-pressed', 'false');
          if (!variant.available) option.setAttribute('aria-label', `${option.textContent}, indisponibila`);
          options.appendChild(option);
        });

        dialog.addEventListener('close', () => {
          activePicker?.trigger?.focus();
          activePicker = null;
        }, { once: true });
        dialog.showModal();
        return;
      }

      const closeButton = target.closest('[data-ebh-size-close]');
      if (closeButton) {
        closePicker(closeButton.closest('dialog'));
        return;
      }

      const option = target.closest('[data-ebh-size-options] .ebh-size-dialog__option');
      if (option && activePicker) {
        const options = option.closest('[data-ebh-size-options]');
        options?.querySelectorAll('.ebh-size-dialog__option').forEach(button => button.setAttribute('aria-pressed', 'false'));
        option.setAttribute('aria-pressed', 'true');
        activePicker.selected = option.dataset.variantId;
        const confirm = activePicker.dialog.querySelector('[data-ebh-size-confirm]');
        if (confirm) confirm.disabled = false;
        return;
      }

      const confirm = target.closest('[data-ebh-size-confirm]');
      if (!confirm || !activePicker?.selected) return;

      const { dialog, trigger, selected } = activePicker;
      const status = dialog.querySelector('[data-ebh-size-status]');
      confirm.disabled = true;
      if (status) {
        status.textContent = 'Se adauga...';
        status.classList.remove('is-error');
      }

      try {
        const response = await fetch(`${storeRoot()}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(selected), quantity: 1 }] })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Produsul nu a putut fi adaugat.');

        const cartResponse = await fetch(`${storeRoot()}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Cosul nu a putut fi actualizat.');
        const cart = await cartResponse.json();

        if (status) status.textContent = 'Adaugat in cos.';
        trigger.classList.add('is-added');
        closePicker(dialog);
        publishCartUpdate(trigger, cart, selected);
        window.setTimeout(() => trigger.classList.remove('is-added'), 1500);
      } catch (error) {
        if (status) {
          status.textContent = error instanceof Error ? error.message : 'A aparut o eroare. Reincearca.';
          status.classList.add('is-error');
        }
        confirm.disabled = false;
      }
    });

    document.addEventListener('click', event => {
      if (event.target instanceof HTMLDialogElement && event.target.matches('[data-ebh-size-dialog]')) {
        closePicker(event.target);
      }
    });
  }
})();
